import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { getImageUrl } from '@apps/main/modules/api/hotels';
import { mapLanguageLocale } from '@apps/main/modules/localization/localeUtils';
import { MarkerType } from '@apps/main/modules/maps/markerFormatters';
import type { MapComponent } from '@apps/main/new-desktop/modules/maps';
import type { MarkerData } from '@apps/main/new-desktop/core/stores/mapStore/mapStore';
import { useMapStore } from '@apps/main/new-desktop/core/stores/mapStore/mapStore';
import {
  useEditingIngredientStore,
  useItineraryStore,
  useTripStore,
} from '@apps/main/new-desktop/core/stores';

import { MapControl } from '@libs/maps';
import type { MapMovedEvent, Map, Marker, MapTypeId } from '@libs/maps';

import type { ActivityMapCardData, HighlightedActivity } from '../../models';
import { useActivitySelectionPanelsStore } from '../activitySelectionPanelsStore';
import { useActivitySelectionStore } from '../activitySelection/activitySelectionStore';
import { sortDestinationProducts } from '../activitySelection/activitySelectionStoreLogic';

import {
  getActivityMapCardData,
  getActivitySelectionMarkers,
  getMarkerFormatters,
  getHighlightedActivity,
  getClusterActivityMarkerFormatters,
  getClusterOptions,
} from './activitySelectionMapStoreLogic';

type MapProperties = InstanceType<typeof MapComponent>['$props'];

export const useActivitySelectionMapStore = defineStore(
  'ActivitySelectionMap',
  () => {
    const mapStore = useMapStore();
    const tripStore = useTripStore();
    const editingIngredientStore = useEditingIngredientStore();
    const activitySelectionPanelsStore = useActivitySelectionPanelsStore();
    const activitySelectionStore = useActivitySelectionStore();
    const itineraryStore = useItineraryStore();

    // State
    const isMapCreated = ref<boolean>(false);
    const isFirstMapRendered = ref<boolean>(true);
    const highlightedActivity = ref<HighlightedActivity | undefined>(undefined);

    // Actions
    function setDynamicMapIntegration(integration: Map): void {
      isFirstMapRendered.value = false;
      isMapCreated.value = true;
      mapStore.setMapIntegration(integration);
    }

    function hightlightOnMap(activityCode: string | undefined): void {
      const ingredientId =
        editingIngredientStore.editingStayIngredient?.ingredientId;
      highlightedActivity.value = getHighlightedActivity(
        activityCode,
        ingredientId
      );
    }

    function markerClicked(marker: Marker): void {
      if (marker.useContentFormatter === MarkerType.Destination) {
        return;
      }
      const data = marker.data as MarkerData;
      highlightedActivity.value = {
        ingredientId: data.ingredientId,
        activityCode: data.hotelCode,
        showActivityMapCard: true,
      };
    }

    function toggleSearchByMovingMap(): void {
      mapStore.updateSearchByMapMovement(!mapStore.searchByMapMovement);
    }

    function mapViewChanged(event: MapMovedEvent): void {
      mapStore.setMapMovedAndCreateNewPolygon(event);
    }

    function hideActivityCard(): void {
      highlightedActivity.value = undefined;
    }

    function activityCardClicked(): void {
      if (!highlightedActivity.value || !activityMapCardData.value) {
        throw new Error('Not possible!');
      }

      const activityId = highlightedActivity.value;

      activitySelectionPanelsStore.openActivityDetailsPanel(
        activityId.activityCode,
        activityId.ingredientId
      );
    }

    function destroyMap(): void {
      mapStore.updateMapShouldCenterOnMarkers(true);
    }

    function setMapTypeId(newMapTypeId: MapTypeId): void {
      mapStore.setMapTypeId(newMapTypeId);
    }

    // Getters
    const mapProperties = computed<MapProperties>(() => ({
      markers: getActivitySelectionMarkers(
        mapStore.markers,
        highlightedActivity.value,
        topAvailableProductCodes.value
      ),
      markerFormatters: getMarkerFormatters(mapStore.mapTypeId),
      clusterMarkerFormatters: getClusterActivityMarkerFormatters(),
      clusterOptions: getClusterOptions(),
      route: mapStore.mapRoute,
      startRectangle: mapStore.DEFAULT_RECTANGLE,
      gestureHandling: 'greedy',
      locale: mapLanguageLocale,
      mapControls: [MapControl.zoomControl, MapControl.mapTypeControl],
      showAllMarkers: mapStore.mapShouldCenterOnMarkers,
    }));

    const activityMapCardData = computed<ActivityMapCardData | undefined>(() =>
      getActivityMapCardData(
        highlightedActivity.value,
        tripStore.getStaticInfo,
        getImageUrl
      )
    );

    const topAvailableProductCodes = computed<string[]>(() => {
      const TOP_NUMBER_ACTIVITIES = 10;
      let selectedHotelCode: string | undefined;

      if (editingIngredientStore.editingStayIngredient?.ingredientId) {
        const hotelFromItinerary = itineraryStore.getHotelIngredientResponse(
          editingIngredientStore.editingStayIngredient.ingredientId
        );

        if (
          hotelFromItinerary &&
          hotelFromItinerary.suggested &&
          hotelFromItinerary.suggested.code
        ) {
          selectedHotelCode = hotelFromItinerary.suggested.code;
        }
      }

      const availableProducts = sortDestinationProducts(
        tripStore.destinationProducts,
        activitySelectionStore.listSuggestionType,
        selectedHotelCode
      );

      return availableProducts
        .map((product) => product.hotelCode)
        .slice(0, TOP_NUMBER_ACTIVITIES + (selectedHotelCode ? 1 : 0));
    });

    return {
      // State
      isMapCreated,
      highlightedActivity,

      // Actions
      setDynamicMapIntegration,
      hightlightOnMap,
      toggleSearchByMovingMap,
      markerClicked,
      mapViewChanged,
      hideActivityCard,
      activityCardClicked,
      destroyMap,
      setMapTypeId,

      // Getters
      mapProperties,
      activityMapCardData,
    };
  }
);
